import { useState, useRef, useCallback, useEffect } from 'react';
import { TALENT_NODES, TALENT_EDGES, getNodeCost, TALENT_NODE_MAP } from '../../data/talentTree';

/**
 * TalentTreeScreen — Path of Exile–style passive talent tree.
 *
 * Props:
 *   onBack           () => void       — go back to idle screen
 *   activatedTalents Set<string>      — set of activated node IDs
 *   talentPoints     number           — unspent talent points (earned by levelling up)
 *   onActivate       (nodeId, cost) => void — activate a node (cost in talent points)
 *   onReset          (refundPts) => void  — reset all talents, refund talent points
 */
export function TalentTreeScreen({ onBack, activatedTalents, talentPoints, onActivate, onReset }) {
  const svgRef = useRef(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.56);
  const [selectedId, setSelectedId] = useState(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const lastMouse = useRef({ x: 0, y: 0 });

  // SVG bounding dimensions — updated on mount and window resize
  const [svgSize, setSvgSize] = useState({ w: 1200, h: 700 });

  useEffect(() => {
    const update = () => {
      if (svgRef.current) {
        const r = svgRef.current.getBoundingClientRect();
        setSvgSize({ w: r.width, h: r.height });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const cx = svgSize.w / 2;
  const cy = svgSize.h / 2;

  // Always treat 'origin' as activated — defensive against empty/stale saves
  const effectiveActivated = activatedTalents.has('origin')
    ? activatedTalents
    : new Set([...activatedTalents, 'origin']);

  const selectedNode = selectedId ? TALENT_NODE_MAP.get(selectedId) : null;
  // Cost = points needed for this specific node (by type), not cumulative
  const cost = selectedNode ? getNodeCost(selectedNode) : 1;
  const paidCount = Math.max(0, effectiveActivated.size - 1);

  // ── Mouse handlers ──────────────────────────────────────────────────────────

  const handleMouseDown = (e) => {
    isDragging.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY };
    lastMouse.current = { x: e.clientX, y: e.clientY };
    if (svgRef.current) svgRef.current._pressing = true;
  };

  const handleMouseMove = (e) => {
    if (!svgRef.current || !svgRef.current._pressing) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    const totalDx = e.clientX - dragStart.current.x;
    const totalDy = e.clientY - dragStart.current.y;
    if (Math.hypot(totalDx, totalDy) > 5) isDragging.current = true;
    if (isDragging.current) {
      setPan(p => ({ x: p.x + dx, y: p.y + dy }));
    }
    lastMouse.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    if (svgRef.current) svgRef.current._pressing = false;
  };

  const handleMouseLeave = () => {
    if (svgRef.current) svgRef.current._pressing = false;
  };

  // Attach wheel listener as non-passive so we can preventDefault
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => Math.max(0.15, Math.min(2.5, z - e.deltaY * 0.001)));
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── Touch handlers (single-finger pan) ─────────────────────────────────────

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      isDragging.current = false;
      dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      if (svgRef.current) svgRef.current._pressing = true;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    if (!svgRef.current || !svgRef.current._pressing) return;
    const tx = e.touches[0].clientX;
    const ty = e.touches[0].clientY;
    const dx = tx - lastMouse.current.x;
    const dy = ty - lastMouse.current.y;
    const totalDx = tx - dragStart.current.x;
    const totalDy = ty - dragStart.current.y;
    if (Math.hypot(totalDx, totalDy) > 5) isDragging.current = true;
    if (isDragging.current) {
      setPan(p => ({ x: p.x + dx, y: p.y + dy }));
    }
    lastMouse.current = { x: tx, y: ty };
  };

  const handleTouchEnd = () => {
    if (svgRef.current) svgRef.current._pressing = false;
  };

  // ── Node click ──────────────────────────────────────────────────────────────
  // No isDragging guard here — stopPropagation on node mousedown prevents the
  // SVG pan logic from interfering, so every node click registers cleanly.
  const handleNodeClick = (nodeId) => {
    setSelectedId(prev => prev === nodeId ? null : nodeId);
  };

  // ── Reset ───────────────────────────────────────────────────────────────────

  const handleReset = () => {
    if (paidCount === 0) return; // nothing beyond origin to reset
    // Compute total points spent on currently activated nodes (for refund)
    const spentPoints = [...effectiveActivated]
      .filter(id => id !== 'origin')
      .reduce((sum, id) => {
        const n = TALENT_NODE_MAP.get(id);
        return sum + (n ? getNodeCost(n) : 1);
      }, 0);
    const confirmed = window.confirm(
      `Reset all talents? You will receive a full refund of ${spentPoints} talent point${spentPoints !== 1 ? 's' : ''}.`
    );
    if (confirmed && onReset) {
      onReset(spentPoints); // pass exact point refund, not node count
      setSelectedId(null);
    }
  };

  // ── Node radius by type ─────────────────────────────────────────────────────

  const getNodeRadius = (type) => {
    switch (type) {
      case 'origin':    return 28;
      case 'keystone':  return 20;
      case 'notable':   return 14;
      case 'small':
      default:          return 9;
    }
  };

  // ── Edge stroke style ───────────────────────────────────────────────────────

  const getEdgeStyle = (id1, id2) => {
    const a1 = effectiveActivated.has(id1);
    const a2 = effectiveActivated.has(id2);
    if (a1 && a2) return { stroke: '#52e084', strokeWidth: 2, opacity: 0.7 };
    // Highlight path edges
    if (pathNodeSet.has(id1) && pathNodeSet.has(id2)) return { stroke: '#ffd700', strokeWidth: 2.5, opacity: 0.9 };
    if ((pathNodeSet.has(id1) && a2) || (pathNodeSet.has(id2) && a1)) return { stroke: '#ffd700', strokeWidth: 2.5, opacity: 0.9 };
    if (a1 || a2) return { stroke: '#2a4a7e', strokeWidth: 1.5, opacity: 0.6 };
    return { stroke: '#1a2a4e', strokeWidth: 1, opacity: 0.4 };
  };

  // ── Path-finding: BFS from selectedNode to closest activated node ──────────

  const findPathToActivated = (startId) => {
    if (!startId || effectiveActivated.has(startId)) return null;
    const visited = new Set([startId]);
    const queue = [[startId, [startId]]];
    while (queue.length > 0) {
      const [current, path] = queue.shift();
      const node = TALENT_NODE_MAP.get(current);
      if (!node) continue;
      for (const neighbor of node.connections) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        const newPath = [...path, neighbor];
        if (effectiveActivated.has(neighbor)) {
          // Return path without the already-activated endpoint
          return newPath.slice(0, -1);
        }
        queue.push([neighbor, newPath]);
      }
    }
    return null; // unreachable
  };

  const pathToActivate = selectedId && !effectiveActivated.has(selectedId)
    ? findPathToActivated(selectedId)
    : null;

  const pathCost = pathToActivate
    ? pathToActivate.reduce((sum, id) => {
        const n = TALENT_NODE_MAP.get(id);
        return sum + (n ? getNodeCost(n) : 1);
      }, 0)
    : cost;

  const pathNodeSet = new Set(pathToActivate || []);

  // ── Info panel derived values ───────────────────────────────────────────────

  const infoNode = selectedNode;
  const infoIsActivated = infoNode ? effectiveActivated.has(infoNode.id) : false;
  const infoIsAvailable = infoNode ? !infoIsActivated && pathToActivate !== null : false;
  const infoCanAfford = talentPoints >= pathCost;
  const infoCanBuy = infoIsAvailable && infoCanAfford;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="tt-screen">
      {/* ── Header ── */}
      <div className="tt-header">
        <button className="back-btn tt-back-btn" onClick={onBack}>← Back</button>
        <span className="tt-title">🌳 Talent Tree</span>
        <span className="tt-points">⭐ {talentPoints} pts available</span>
        <span className="tt-node-count">
          {paidCount} / {TALENT_NODES.length - 1} nodes activated
        </span>
        <button
          className="tt-reset-btn"
          onClick={handleReset}
          disabled={paidCount === 0}
        >
          🔄 Reset
        </button>
      </div>

      {/* ── SVG canvas ── */}
      <div className="tt-canvas-container">
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          overflow="hidden"
          className="tt-svg"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ cursor: isDragging.current ? 'grabbing' : 'grab', display: 'block' }}
        >
          <g transform={`translate(${cx + pan.x}, ${cy + pan.y}) scale(${zoom})`}>

            {/* ── Edges ── */}
            {TALENT_EDGES.map(edge => {
              const [id1, id2] = edge;
              const node1 = TALENT_NODE_MAP.get(id1);
              const node2 = TALENT_NODE_MAP.get(id2);
              if (!node1 || !node2) return null;
              const style = getEdgeStyle(id1, id2);
              return (
                <line
                  key={`${id1}-${id2}`}
                  x1={node1.x}
                  y1={node1.y}
                  x2={node2.x}
                  y2={node2.y}
                  stroke={style.stroke}
                  strokeWidth={style.strokeWidth}
                  opacity={style.opacity}
                />
              );
            })}

            {/* ── Nodes ── */}
            {TALENT_NODES.map(node => {
              const isActivated = effectiveActivated.has(node.id);
              const isAvailable = !isActivated &&
                node.connections.some(cid => effectiveActivated.has(cid));
              const isSelected = selectedId === node.id;
              const r = getNodeRadius(node.type);

              // Build filter for activated/available glow
              const filterStr = isActivated
                ? `drop-shadow(0 0 8px ${node.color})`
                : isAvailable
                  ? `drop-shadow(0 0 5px ${node.color})`
                  : undefined;

              const isOnPath = pathNodeSet.has(node.id);

              // Circle fill / stroke based on state
              let fill, stroke, strokeWidth, opacity, extraClass;
              if (isActivated) {
                fill = node.color;
                stroke = 'white';
                strokeWidth = 1.5;
                opacity = 1;
                extraClass = '';
              } else if (isOnPath) {
                fill = '#1a1200';
                stroke = '#ffd700';
                strokeWidth = 2.5;
                opacity = 1;
                extraClass = 'tt-node-available';
              } else if (isAvailable) {
                fill = '#0d1e3a';
                stroke = node.color;
                strokeWidth = 2.5;
                opacity = 1;
                extraClass = 'tt-node-available';
              } else {
                fill = '#0a1020';
                stroke = '#2a3a5e';
                strokeWidth = 1;
                opacity = 0.45;
                extraClass = '';
              }

              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x}, ${node.y})`}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => handleNodeClick(node.id)}
                >
                  {/* Selection ring */}
                  {isSelected && (
                    <circle
                      r={r + 5}
                      fill="none"
                      stroke="white"
                      strokeWidth={1.5}
                      opacity={0.8}
                    />
                  )}

                  {/* Main circle */}
                  <circle
                    r={r}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    opacity={opacity}
                    className={extraClass}
                    style={filterStr ? { filter: filterStr } : undefined}
                  />

                  {/* Origin: centered icon + label below */}
                  {node.type === 'origin' && (
                    <>
                      <text
                        fontSize={22}
                        textAnchor="middle"
                        dominantBaseline="central"
                        y={0}
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        {node.icon}
                      </text>
                      <text
                        fontSize={11}
                        fill={node.color}
                        textAnchor="middle"
                        y={r + 16}
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        {node.label}
                      </text>
                    </>
                  )}

                  {/* Keystone: icon above + label below */}
                  {node.type === 'keystone' && zoom >= 0.4 && (
                    <>
                      <text
                        fontSize={16}
                        textAnchor="middle"
                        y={-r - 8}
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        {node.icon}
                      </text>
                      <text
                        fontSize={11}
                        fill={node.color}
                        textAnchor="middle"
                        y={r + 16}
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        {node.label}
                      </text>
                    </>
                  )}

                  {/* Notable: label below */}
                  {node.type === 'notable' && zoom >= 0.4 && (
                    <text
                      fontSize={9}
                      fill={node.color}
                      textAnchor="middle"
                      y={r + 14}
                      style={{ userSelect: 'none', pointerEvents: 'none' }}
                    >
                      {node.label}
                    </text>
                  )}
                </g>
              );
            })}

          </g>
        </svg>

        {/* ── Info panel (bottom-left overlay) ── */}
        {infoNode && (
          <div className="tt-info-panel">
            <div className="tt-info-icon">{infoNode.icon}</div>
            <div className="tt-info-name" style={{ color: infoNode.color }}>
              {infoNode.label}
            </div>
            <div className="tt-info-type">
              {infoNode.type} node &bull; {infoNode.branch} branch
            </div>
            <div className="tt-info-desc">{infoNode.desc}</div>
            <div className="tt-info-cost">
              {infoIsActivated
                ? `Cost: ⭐ ${cost} ${cost === 1 ? 'point' : 'points'}`
                : pathToActivate && pathToActivate.length > 1
                  ? `Path: ${pathToActivate.length} nodes · ⭐ ${pathCost} ${pathCost === 1 ? 'point' : 'points'} total`
                  : `Cost: ⭐ ${pathCost} ${pathCost === 1 ? 'point' : 'points'}`}
            </div>
            <button
              className={`tt-buy-btn ${infoCanBuy ? 'tt-buy-btn--enabled' : 'tt-buy-btn--disabled'}`}
              onClick={() => {
                if (!infoCanBuy) return;
                if (pathToActivate && pathToActivate.length > 0) {
                  // Activate all nodes along the path in order (from activated end to selected)
                  const reversed = [...pathToActivate].reverse();
                  for (const nodeId of reversed) {
                    const n = TALENT_NODE_MAP.get(nodeId);
                    const c = n ? getNodeCost(n) : 1;
                    onActivate(nodeId, c);
                  }
                } else {
                  onActivate(infoNode.id, cost);
                }
              }}
              disabled={!infoCanBuy}
            >
              {infoIsActivated
                ? '✅ Already Activated'
                : !infoIsAvailable
                  ? '🔒 No Path to Node'
                  : !infoCanAfford
                    ? `⭐ Need ${pathCost - talentPoints} more pts`
                    : pathToActivate && pathToActivate.length > 1
                      ? `⚡ Activate Path (${pathCost} pt${pathCost !== 1 ? 's' : ''})`
                      : `⚡ Activate (${pathCost} pt${pathCost !== 1 ? 's' : ''})`}
            </button>
          </div>
        )}

        {/* ── Legend (bottom-right overlay) ── */}
        <div className="tt-legend">
          <div className="tt-legend-item">
            <span style={{ color: '#ffd700' }}>★</span> Origin
          </div>
          <div className="tt-legend-item">
            <span
              style={{
                background: '#4a4a4a',
                borderRadius: 50,
                display: 'inline-block',
                width: 18,
                height: 18,
                verticalAlign: 'middle',
              }}
            />
            {' '}Keystone
          </div>
          <div className="tt-legend-item">&#9675; Notable</div>
          <div className="tt-legend-item">&middot; Small</div>
          <div className="tt-legend-item" style={{ color: '#52e084' }}>
            &#9679; Activated
          </div>
          <div className="tt-legend-item" style={{ color: '#7af5ff' }}>
            &#9675; Available
          </div>
        </div>
      </div>
    </div>
  );
}
